/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useSprings, animated, to as interpolate } from 'react-spring';
import { useDrag } from 'react-use-gesture';

import Metrics from '@spotahome/soyuz-metrics';

import { trans, getPagePath } from '@spotahome/soyuz/client';

import Homecard from '@common/components/Homecard/Homecard';
import { useMatcherContext } from '@common/context/MatcherContext';
import { getRentableUnitPrice } from '@common/utils/paymentsAdapter';
import getGqlClient from '@gqlClient/client';
import { addFavorite } from '@gqlClient/mutations/favorites';

import { getHomecardImageSet } from '../../../utils/homecards';
import IconLoading from '../../../../LEGACY/javascripts/ui/icon/icon-loading';

import SwiperNav from './SwiperNav';
import SwiperButtons from './SwiperButtons';
import SwiperFooter from './SwiperFooter';
import SwipeInstructions from './SwipeInstructions';

import './Swiper.scss';

const MAX_FLICKS_LIMIT = 15;

/*
 * Animations created with react-spring & react-use-gesture
 * https://use-gesture.netlify.com/docs/introduction
 * https://www.react-spring.io/docs/hooks/basics
 */

const LIKE = 'like';
const DISLIKE = 'dislike';

const to = i => ({
  x: 0,
  rot: 0,
  delay: i * 100
});

const from = () => ({
  x: 0,
  rot: 0
});

const parseHomecardProps = homecard => ({
  ...homecard,
  imageSet: homecard.photoUrls ? getHomecardImageSet(homecard.photoUrls) : '',
  price: getRentableUnitPrice(
    homecard.monthlyPrice.type,
    homecard.monthlyPrice.minimumPrice,
    homecard.currencyIsoCode,
    homecard.monthlyPrice.pricesByMonth,
    homecard.monthlyPrice.fixedPrice,
    true
  ),
  isMinimalRoundedCard: true
});

const rotateTransform = r => `rotateZ(${r}deg)`;

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const getHomecardHash = homecardList => homecardList.map(h => h.id).join('');

const Swiper = ({ close, homecards, next, goToFilters }) => {
  const { requestNextHomecards } = useMatcherContext();
  const prevHomecardHash = usePrevious(getHomecardHash(homecards));
  const [cardsFlicked, setCardsFlicked] = useState(0);
  const [mobileWidth, setMobileWidth] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);

  useEffect(() => {
    Metrics.ga.sendEvent('Matcher', 'Swiper', 'pageview');
  }, []);

  const [props, set] = useSprings(homecards.length, i => ({
    ...to(i),
    from: from(i)
  }));

  const gone = new Set();

  const markAsFavorite = async rentableUnitId => {
    try {
      await addFavorite(getGqlClient())(rentableUnitId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleLikedProperty = rentableUnitId => {
    setFavoritesCount(favoritesCount + 1);
    Metrics.ga.sendEvent('Matcher', 'liked rentable unit', rentableUnitId);
    markAsFavorite(rentableUnitId);
  };

  const handleDiscardedProperty = rentableUnitId => {
    Metrics.ga.sendEvent('Matcher', 'discarded rentable unit', rentableUnitId);
  };

  const createHandleSwipeByClick = (action, callback) => () => {
    const cardsFlickedOffset = cardsFlicked + 1;
    const lastHomecard = homecards.length - cardsFlickedOffset;

    set(i => {
      // Only do it for the last item
      if (i !== lastHomecard) return;

      gone.add(lastHomecard);
      const direction = action === LIKE ? 1 : -1;
      const x = (200 + mobileWidth) * direction;
      const rot = 0;

      return {
        x,
        rot,
        delay: undefined,
        config: {
          friction: 50,
          tension: 200
        }
      };
    });
    setCardsFlicked(cardsFlickedOffset);
    callback(homecards[lastHomecard].id);
  };

  const handleGoToFavorites = () => {
    Metrics.ga.sendEvent('Matcher', 'swiper go to favorites', 'button click');
    window.location = getPagePath('marketplace.favorites');
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setMobileWidth(window.innerWidth);
    }
  }, [mobileWidth]);

  useEffect(() => {
    if (favoritesCount >= MAX_FLICKS_LIMIT) {
      next();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritesCount]);

  useEffect(() => {
    if (homecards.length === cardsFlicked) {
      requestNextHomecards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsFlicked]);

  useEffect(() => {
    const currentHash = getHomecardHash(homecards);
    if (prevHomecardHash !== currentHash) {
      gone.clear();
      setCardsFlicked(0);
      set(() => to(0));
    }
  }, [gone, homecards, prevHomecardHash, set]);

  const bind = useDrag(
    ({ args: [index], down, movement: [mx], direction: [xDir], velocity }) => {
      const trigger = velocity > 0.2; // If you flick hard enough it should trigger the card to fly out
      const direction = xDir < 0 ? -1 : 1; // Direction should either point left or right

      if (!down && trigger) {
        gone.add(index); // If finger is up and trigger velocity is reached, we flag the card ready to fly out
      }

      set(i => {
        if (index !== i) return; // We're only interested in changing spring-data transformations for the current card

        const isGone = gone.has(index);
        const x = isGone ? (200 + mobileWidth) * direction : down ? mx : 0; // When a card is gone it flys out left or right, otherwise goes back to zero
        const rot = mx / 100 + (isGone ? direction * 30 * velocity : 0); // How much the card tilts, flicking it harder makes it rotate faster

        if (isGone) {
          setCardsFlicked(cardsFlicked + 1);

          const rentableUnitId = homecards[index].id;
          if (xDir > 0) {
            handleLikedProperty(rentableUnitId);
          } else if (xDir < 0) {
            handleDiscardedProperty(rentableUnitId);
          }
        }

        return {
          x,
          rot,
          delay: undefined,
          config: {
            friction: 50,
            tension: down ? 800 : isGone ? 200 : 500
          }
        };
      });

      if (!down && gone.size === homecards.length) {
        // @TO-DO: Show a design saying change filters
        // @ Xavi Amoros
      }
    }
  );

  return (
    <div className="swiper">
      <SwipeInstructions />
      <SwiperNav onFiltersPressed={goToFilters} onExitPressed={close} />

      <div className={'swiper--loading'}>
        <IconLoading />
        <p>{trans('loading')}</p>
      </div>

      <div className="swiper-wrapper">
        {props
          .filter((_, i) => homecards[i])
          .map(({ x, y, rot }, i) => (
            <animated.div key={i} style={{ x, y }} className="swiper-card">
              <animated.div
                {...bind(i)}
                style={{ transform: interpolate([rot], rotateTransform) }}
              >
                <Homecard {...parseHomecardProps(homecards[i])} />
              </animated.div>
            </animated.div>
          ))}
      </div>
      <SwiperButtons
        disabled={cardsFlicked === homecards.length}
        handleDiscardedProperty={createHandleSwipeByClick(
          DISLIKE,
          handleDiscardedProperty
        )}
        handleLikedProperty={createHandleSwipeByClick(
          LIKE,
          handleLikedProperty
        )}
      />
      <SwiperFooter
        favoritesCount={favoritesCount}
        handleGoToFavorites={handleGoToFavorites}
      />
    </div>
  );
};

Swiper.propTypes = {
  close: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  goToFilters: PropTypes.func.isRequired
};

export default Swiper;
